/**
 * Integrytas Group - Common Security Module (CSM)
 * useTaskQueue — background task submission and real-time progress monitoring.
 *
 * Flow:
 *   1. submitTask() inserts a row into csm_task_queue (PENDING)
 *      and calls the csm-task-worker Edge Function (fire-and-forget).
 *   2. The worker runs server-side, updating progress in the DB.
 *   3. The Supabase Realtime subscription picks up changes and updates
 *      local state — the TaskMonitor component re-renders automatically.
 *
 * The context is provided once in TabManagerShell so any page in the shell
 * can call useTaskQueue() to submit a task or read active task state.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiDb } from './api_client';
import { supabase } from './supabase_client';
import { subscribeRealtime } from './realtime';
import type { AuthSession } from '../types/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TaskStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface Task {
  task_id:      number;
  task_type:    string;
  label:        string | null;
  status:       TaskStatus;
  progress:     number;
  error_message: string | null;
  result:       Record<string, unknown> | null;
  created_at:   string;
  started_at:   string | null;
  completed_at: string | null;
}

export interface TaskQueueCtx {
  tasks:      Task[];
  activeTasks: Task[];
  submitTask: (
    type:    string,
    label:   string,
    payload: Record<string, unknown>,
  ) => Promise<number | null>;
  cancelTask: (taskId: number) => Promise<void>;
}

export const TaskQueueContext = createContext<TaskQueueCtx>({
  tasks:       [],
  activeTasks: [],
  submitTask:  async () => null,
  cancelTask:  async () => { /* no-op default; replaced by the provider */ },
});

const WORKER_URL = `${import.meta.env.VITE_SUPABASE_URL as string}/functions/v1/csm-task-worker`;
const MAX_HISTORY = 30;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTaskQueueState(session: AuthSession): TaskQueueCtx {
  const userId = session.effective_user.user_id;
  const [tasks, setTasks] = useState<Task[]>([]);

  // Load recent tasks on mount.
  useEffect(() => {
    apiDb
      .from('csm_task_queue')
      .select('task_id,task_type,label,status,progress,error_message,result,created_at,started_at,completed_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(MAX_HISTORY)
      .then(({ data }) => setTasks((data ?? []) as Task[]));
  }, [userId]);

  // Real-time updates from the worker.
  useEffect(() => {
    return subscribeRealtime(() => supabase
      .channel(`csm_tasks_${userId}`)
      .on(
        'postgres_changes' as Parameters<ReturnType<typeof supabase.channel>['on']>[0],
        {
          event:  '*',
          schema: 'public',
          table:  'csm_task_queue',
          filter: `user_id=eq.${userId}`,
        },
        (payload: { new?: Task; old?: Task; eventType?: string }) => {
          const row = (payload.new ?? payload.old) as Task;
          if (!row) return;
          setTasks(prev => {
            const idx = prev.findIndex(t => t.task_id === row.task_id);
            if (payload.eventType === 'DELETE') return prev.filter(t => t.task_id !== row.task_id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = row;
              return next;
            }
            return [row, ...prev].slice(0, MAX_HISTORY);
          });
        }
      ));
  }, [userId]);

  const submitTask = useCallback(async (
    type:    string,
    label:   string,
    payload: Record<string, unknown>,
  ): Promise<number | null> => {
    // Insert into DB via RPC.
    const { data: taskId, error } = await apiDb.rpc('csm_submit_task', {
      p_user_id:   userId,
      p_task_type: type,
      p_label:     label,
      p_payload:   payload,
    });

    if (error || !taskId) {
      console.error('Failed to submit task:', error?.message);
      return null;
    }

    // Fire-and-forget to the Edge Function worker.
    const { data: sd } = await supabase.auth.getSession();
    const token = sd.session?.access_token ?? '';

    void fetch(WORKER_URL, {
      method:  'POST',
      headers: {
        'content-type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ task_id: taskId }),
    }).catch(err => console.warn('Task worker invocation error:', err));

    return taskId as number;
  }, [userId]);

  const cancelTask = useCallback(async (taskId: number) => {
    await apiDb
      .from('csm_task_queue')
      .update({ status: 'CANCELLED' })
      .eq('task_id', taskId)
      .eq('user_id', userId);
  }, [userId]);

  const activeTasks = tasks.filter(t =>
    t.status === 'PENDING' || t.status === 'RUNNING'
  );

  return { tasks, activeTasks, submitTask, cancelTask };
}

export function useTaskQueue(): TaskQueueCtx {
  return useContext(TaskQueueContext);
}
